import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useRefData } from "@/context/RefDataContext";
import { StatusBadge, fmtDate } from "@/components/shared";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  ArrowLeft, CalendarDays, Clock, MapPin, User, GraduationCap, CheckCircle2,
  Check, X, Clock3, ShieldCheck, ClipboardList, StickyNote, Plus,
} from "lucide-react";
import { toast } from "sonner";

const ATT = [
  { v: "present", icon: Check, cls: "text-green-600", testid: "att-present" },
  { v: "absent", icon: X, cls: "text-destructive", testid: "att-absent" },
  { v: "late", icon: Clock3, cls: "text-amber-600", testid: "att-late" },
  { v: "excused", icon: ShieldCheck, cls: "text-blue-600", testid: "att-excused" },
];

export default function ClassDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const ref = useRefData();
  const canStaff = user?.role !== "client";
  const [c, setC] = useState(null);
  const [err, setErr] = useState(false);
  const [notes, setNotes] = useState("");
  const [enrollDog, setEnrollDog] = useState("");
  const [hwOpen, setHwOpen] = useState(false);
  const [hwMode, setHwMode] = useState("single");
  const [hw, setHw] = useState({ dog_id: "", lesson_id: "", title: "", instructions: "", video_url: "", due_date: "" });
  const [templates, setTemplates] = useState([]);

  const selectLesson = async (lessonId) => {
    setHw((prev) => ({ ...prev, lesson_id: lessonId }));
    setTemplates([]);
    if (!lessonId) return;
    try { const r = await api.get(`/lessons/${lessonId}/homework-templates`); setTemplates(r.data); } catch { setTemplates([]); }
  };
  const applyTemplate = (t) => setHw((prev) => ({ ...prev, title: t.title, instructions: t.instructions || "", video_url: t.video_url || "" }));

  const load = useCallback(async () => {
    try { const r = await api.get(`/classes/${id}/detail`); setC(r.data); setNotes(r.data.notes || ""); }
    catch (e) { setErr(true); }
  }, [id]);
  useEffect(() => { load(); }, [load]);

  if (err) return <div className="text-center py-20 text-muted-foreground">Class not found.</div>;
  if (!c) return <div className="text-center py-20 text-muted-foreground">Loading...</div>;

  const mark = async (dogId, status) => {
    const cur = c.enrollments.find((e) => e.dog_id === dogId);
    await api.post(`/classes/${id}/attendance`, { dog_id: dogId, attendance_status: status, notes: cur?.notes || "" });
    await load();
  };
  const saveNote = async (dogId, note) => {
    const cur = c.enrollments.find((e) => e.dog_id === dogId);
    await api.post(`/classes/${id}/attendance`, { dog_id: dogId, attendance_status: cur?.attendance_status || "pending", notes: note });
    toast.success("Note saved");
  };
  const saveClassNotes = async () => { await api.patch(`/classes/${id}/session`, { notes }); toast.success("Class notes saved"); };
  const complete = async () => { await api.patch(`/classes/${id}/session`, { status: "completed" }); toast.success("Class completed"); await load(); };
  const enroll = async () => { if (!enrollDog) return; await api.post(`/classes/${id}/enroll`, { dog_id: enrollDog }); setEnrollDog(""); await load(); };

  const openHw = (dogId) => { setHwMode("single"); setHw({ dog_id: dogId || "", lesson_id: "", title: "", instructions: "", video_url: "", due_date: "" }); setTemplates([]); setHwOpen(true); };
  const openHwAll = () => { setHwMode("all"); setHw({ dog_id: "", lesson_id: "", title: "", instructions: "", video_url: "", due_date: "" }); setTemplates([]); setHwOpen(true); };
  const saveHw = async () => {
    try {
      if (hwMode === "all") await api.post(`/classes/${id}/assign-homework`, { title: hw.title, instructions: hw.instructions, video_url: hw.video_url, due_date: hw.due_date, lesson_id: hw.lesson_id || null });
      else await api.post("/homework", { ...hw, class_id: id });
      toast.success("Homework assigned"); setHwOpen(false); await load();
    } catch (e) { toast.error("Failed"); }
  };

  return (
    <div>
      <Button variant="ghost" size="sm" className="gap-2 mb-4 -ml-2 text-muted-foreground" onClick={() => navigate("/classes")} data-testid="class-back"><ArrowLeft size={16} /> Back to Classes</Button>

      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-semibold tracking-tight">{c.name}</h1>
          <div className="flex flex-wrap gap-4 mt-1.5 text-sm text-muted-foreground">
            <span className="flex items-center gap-1.5"><CalendarDays size={14} /> {fmtDate(c.date)}</span>
            <span className="flex items-center gap-1.5"><Clock size={14} /> {c.time}</span>
            <span className="flex items-center gap-1.5"><MapPin size={14} /> {c.location_name}</span>
            <span className="flex items-center gap-1.5"><User size={14} /> {c.trainer_name}</span>
            <span className="flex items-center gap-1.5"><GraduationCap size={14} /> {c.program_name}</span>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs text-muted-foreground">{c.enrolled_count}/{c.capacity} enrolled · {c.available_spots} open{c.waitlist_count ? ` · ${c.waitlist_count} waitlisted` : ""}</span>
          <StatusBadge value={c.effective_status || c.status} />
          {canStaff && c.status !== "completed" && (
            <Button className="gap-2" onClick={complete} disabled={c.enrolled_count > 0 && !c.attendance_complete} title={c.enrolled_count > 0 && !c.attendance_complete ? "Mark attendance for all dogs first" : ""} data-testid="class-complete"><CheckCircle2 size={16} /> Mark completed</Button>
          )}
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          {/* Enrolled dogs + attendance */}
          <Card className="border-border">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="font-heading text-lg">Enrolled Dogs ({c.enrolled_count}/{c.capacity})</CardTitle>
              {canStaff && (
                <div className="flex gap-2">
                  <Select value={enrollDog} onValueChange={setEnrollDog}>
                    <SelectTrigger className="w-44 h-9" data-testid="enroll-dog-select"><SelectValue placeholder="Enroll dog" /></SelectTrigger>
                    <SelectContent>{ref.dogs.map((d) => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}</SelectContent>
                  </Select>
                  <Button size="sm" onClick={enroll} data-testid="enroll-btn">Enroll</Button>
                </div>
              )}
            </CardHeader>
            <CardContent className="space-y-3">
              {c.enrollments.length ? c.enrollments.map((e) => (
                <div key={e.id} className="p-3 rounded-lg border border-border/60" data-testid="class-enrollment">
                  <div className="flex items-center justify-between">
                    <div><div className="font-medium text-sm">{e.dog_name}</div><div className="text-xs text-muted-foreground">{e.breed}</div></div>
                    <div className="flex items-center gap-1.5">
                      <StatusBadge value={e.attendance_status} />
                      {canStaff && ATT.map((a) => (
                        <Button key={a.v} variant="outline" size="icon" className="h-7 w-7" onClick={() => mark(e.dog_id, a.v)} data-testid={a.testid} title={a.v}>
                          <a.icon size={13} className={a.cls} />
                        </Button>
                      ))}
                      {canStaff && <Button variant="outline" size="sm" onClick={() => openHw(e.dog_id)} data-testid="dog-assign-hw">+ HW</Button>}
                    </div>
                  </div>
                  {canStaff && (
                    <Input defaultValue={e.notes} placeholder="Dog note for this class..." className="mt-2 h-8 text-sm"
                      onBlur={(ev) => ev.target.value !== (e.notes || "") && saveNote(e.dog_id, ev.target.value)} data-testid="dog-note-input" />
                  )}
                </div>
              )) : <p className="text-sm text-muted-foreground">No dogs enrolled.</p>}
            </CardContent>
          </Card>

          {/* Class notes */}
          <Card className="border-border">
            <CardHeader><CardTitle className="font-heading text-lg flex items-center gap-2"><StickyNote size={18} strokeWidth={1.5} /> Class Notes</CardTitle></CardHeader>
            <CardContent>
              <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Summary of the session..." disabled={!canStaff} data-testid="class-notes-input" />
              {canStaff && <Button className="mt-3" onClick={saveClassNotes} data-testid="class-notes-save">Save notes</Button>}
            </CardContent>
          </Card>

          {/* Homework */}
          <Card className="border-border">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="font-heading text-lg flex items-center gap-2"><ClipboardList size={18} strokeWidth={1.5} /> Homework</CardTitle>
              {canStaff && <div className="flex gap-2">
                <Button size="sm" variant="outline" className="gap-1.5" onClick={openHwAll} data-testid="class-assign-hw-all"><Plus size={14} /> All present</Button>
                <Button size="sm" variant="outline" className="gap-1.5" onClick={() => openHw("")} data-testid="class-assign-hw"><Plus size={14} /> Assign</Button>
              </div>}
            </CardHeader>
            <CardContent className="space-y-2">
              {c.homework?.length ? c.homework.map((h) => (
                <div key={h.id} className="flex items-center justify-between p-3 rounded-lg border border-border/60">
                  <div><div className="text-sm font-medium">{h.title}</div><div className="text-xs text-muted-foreground">Due {fmtDate(h.due_date)}</div></div>
                  <StatusBadge value={h.status} />
                </div>
              )) : <p className="text-sm text-muted-foreground">No homework assigned for this class.</p>}
            </CardContent>
          </Card>
        </div>

        {/* Program lessons */}
        <Card className="border-border">
          <CardHeader><CardTitle className="font-heading text-lg flex items-center gap-2"><GraduationCap size={18} strokeWidth={1.5} /> {c.program_name || "Program"}</CardTitle></CardHeader>
          <CardContent>
            <div className="text-xs uppercase tracking-wide font-semibold text-muted-foreground mb-2">Lessons</div>
            <ol className="space-y-1.5">
              {c.lessons?.length ? c.lessons.map((l, i) => (
                <li key={l.id || i} className="flex items-start gap-2 text-sm">
                  <span className="h-5 w-5 rounded-full bg-primary text-primary-foreground text-[11px] flex items-center justify-center shrink-0 mt-0.5">{i + 1}</span>
                  <span className="font-medium">{l.title}</span>
                </li>
              )) : <p className="text-sm text-muted-foreground">No lessons.</p>}
            </ol>
          </CardContent>
        </Card>

        {c.dogs_needing_followup?.length > 0 && (
          <Card className="border-amber-200 bg-amber-50/50">
            <CardHeader><CardTitle className="font-heading text-base">Dogs Needing Follow-up</CardTitle></CardHeader>
            <CardContent className="space-y-1" data-testid="followup-list">
              {c.dogs_needing_followup.map((d) => <div key={d.dog_id} className="text-sm flex items-center gap-2"><span className="h-1.5 w-1.5 rounded-full bg-amber-500" /> {d.dog_name}</div>)}
            </CardContent>
          </Card>
        )}
      </div>

      {/* Assign homework dialog */}
      <Dialog open={hwOpen} onOpenChange={setHwOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading">Assign Homework</DialogTitle>
            <DialogDescription>Assign a homework task for a dog enrolled in this class.</DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-2">
            {hwMode === "single" ? (
              <div className="grid gap-1.5"><Label>Dog</Label>
                <Select value={hw.dog_id} onValueChange={(v) => setHw({ ...hw, dog_id: v })}>
                  <SelectTrigger data-testid="hw-dog"><SelectValue placeholder="Select dog" /></SelectTrigger>
                  <SelectContent>{c.enrollments.map((e) => <SelectItem key={e.dog_id} value={e.dog_id}>{e.dog_name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            ) : (
              <p className="text-sm text-muted-foreground rounded-lg bg-muted/50 p-3">This homework will be assigned to all <b>present</b> dogs in this class.</p>
            )}
            <div className="grid gap-1.5"><Label>Lesson</Label>
              <Select value={hw.lesson_id} onValueChange={selectLesson}>
                <SelectTrigger data-testid="hw-lesson"><SelectValue placeholder="Select lesson" /></SelectTrigger>
                <SelectContent>{(c.lessons || []).map((l) => <SelectItem key={l.id} value={l.id}>{l.title}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            {templates.length > 0 && (
              <div className="grid gap-1.5"><Label>Use homework template</Label>
                <Select value="" onValueChange={(tid) => { const t = templates.find((x) => x.id === tid); if (t) applyTemplate(t); }}>
                  <SelectTrigger data-testid="hw-template"><SelectValue placeholder="Prefill from HV Method template" /></SelectTrigger>
                  <SelectContent>{templates.map((t) => <SelectItem key={t.id} value={t.id}>{t.title}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            )}
            <div className="grid gap-1.5"><Label>Title</Label><Input value={hw.title} onChange={(e) => setHw({ ...hw, title: e.target.value })} data-testid="hw-title" /></div>
            <div className="grid gap-1.5"><Label>Instructions</Label><Textarea value={hw.instructions} onChange={(e) => setHw({ ...hw, instructions: e.target.value })} data-testid="hw-instructions" /></div>
            <div className="grid grid-cols-2 gap-4">
              <div className="grid gap-1.5"><Label>Video / link</Label><Input value={hw.video_url} onChange={(e) => setHw({ ...hw, video_url: e.target.value })} placeholder="Optional" data-testid="hw-video" /></div>
              <div className="grid gap-1.5"><Label>Due date</Label><Input type="date" value={hw.due_date} onChange={(e) => setHw({ ...hw, due_date: e.target.value })} data-testid="hw-due" /></div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setHwOpen(false)}>Cancel</Button>
            <Button onClick={saveHw} disabled={(hwMode === "single" && !hw.dog_id) || !hw.title} data-testid="hw-save">Assign</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
